import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import { staleGuard } from "src/utils/staleGuard";

// ----------------------------------------------------------------------
//
// Estado del sistema de impresión del restaurante:
//   - `workers`: lista de workers (Windows / Android) vistos por el backend.
//   - `printJobs`: últimas emisiones hacia los workers (últimas 24h).
//   - `summary`: conteo por estado de los printJobs (para chips de resumen).
//
// Se alimenta de dos fuentes:
//   1) Fetch REST inicial al abrir el panel + refresh manual.
//   2) Push en vivo por SignalR (WorkerStatusHub) → upsertWorker.
//
// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  hasError: false,
  errorMessage: null,
  workers: [],
  printJobs: [],
  summary: {},
  lastFetchedAt: null,
  // Fingerprint del set de incidencias de impresora que el usuario ha
  // dismisseado. Mientras el set actual coincida con éste, el banner
  // permanece oculto. Si aparece una incidencia nueva (impresora
  // distinta, o cambia el motivo de error de una existente) el
  // fingerprint cambia y el banner vuelve a mostrarse — el usuario
  // necesita verlo, es información nueva.
  dismissedPrinterIssuesFingerprint: null,
};

const slice = createSlice({
  name: "workerStatus",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.hasError = false;
      state.errorMessage = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.hasError = true;
      state.errorMessage = action.payload;
    },
    fetchWorkersSuccess(state, action) {
      state.isLoading = false;
      state.workers = action.payload || [];
      state.lastFetchedAt = new Date().toISOString();
    },
    fetchPrintJobsSuccess(state, action) {
      state.isLoading = false;
      state.printJobs = action.payload?.recent || [];
      state.summary = action.payload?.summary || {};
      state.lastFetchedAt = new Date().toISOString();
    },
    /**
     * Upsert por instanceId. Se llama desde el listener SignalR cada vez que
     * el worker manda un heartbeat. Si no existe, añade; si existe, reemplaza.
     */
    upsertWorker(state, action) {
      const incoming = action.payload;
      if (!incoming?.instanceId) return;
      const idx = state.workers.findIndex(
        (w) => w.instanceId === incoming.instanceId,
      );
      if (idx === -1) state.workers.push(incoming);
      else state.workers[idx] = incoming;
    },
    dismissPrinterIssues(state, action) {
      state.dismissedPrinterIssuesFingerprint = action.payload || null;
    },
  },
});

export default slice.reducer;

export const { upsertWorker, dismissPrinterIssues } = slice.actions;

// ----------------------------------------------------------------------
// Thunks

export function fetchWorkerStatus({ force = false } = {}) {
  return async (dispatch) => {
    // WorkerStatusHub (SignalR) empuja los cambios en tiempo real
    // (workerStatus/{id}). Con staleGuard evitamos el GET redundante en
    // cada remount — el estado se mantiene fresco vía el push.
    if (!force && staleGuard("workerStatus/list")) return;
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get("/worker/status");
      dispatch(slice.actions.fetchWorkersSuccess(response.data));
    } catch (error) {
      dispatch(
        slice.actions.hasError(error?.message || "Error cargando workers"),
      );
    }
  };
}

export function fetchPrintJobs(idRestaurante, limit = 50) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      // HealthController vive fuera del prefijo /api del axios instance (tiene
      // Route("/") + HttpGet("api/health/print-jobs/...")), así que baseURL termina
      // en /api y la ruta empieza con "/health/print-jobs/...".
      const response = await axios.get(
        `/health/print-jobs/${idRestaurante}?limit=${limit}`,
      );
      dispatch(slice.actions.fetchPrintJobsSuccess(response.data));
    } catch (error) {
      dispatch(
        slice.actions.hasError(error?.message || "Error cargando print jobs"),
      );
    }
  };
}
